import moment, { Moment } from 'moment'
import { multiSelect } from 'src/common/selectors'
import { obterDataValida } from 'src/utils/formatadoras/formatadoras'
import i18n from '@/typescript/servicos/i18n/i18n.servico'
import { StatusSolicitacao } from '../dominio/gerenciamentoDeFerias.interface'
import { TranslateResult } from 'vue-i18n'
import type { ActionContext } from 'vuex'
import CalendarioDeFeriasState, { IStateCalendarioDeFerias } from './CalendarioDeFerias/state'

export type IAbaDeEntradaGestaoDeFerias = 'liderados-solicitadas' | 'liderados-agendadas'

export interface IContextoDeEntradaGestaoDeFerias {
  aba: IAbaDeEntradaGestaoDeFerias
  empregado: {
    id: string
    name: string
  }
  dataInicial: Date
  dataFinal: Date
  buscarAutomaticamente: boolean
}

/**
 * Interface que define a estrutura de uma categoria
 * @property name - Nome exibido da categoria (traduzido)
 * @property id - Identificador único da categoria
 * @property common - Endpoint comum para API
 * @property state - Nome do estado no Vuex
 * @property entity - Tipo de entidade relacionada
 */
interface Categoria {
  name: string
  id: string
  common: string
  state: string
  entity: string
}

interface IStatusSolicitacao {
  chave: string,
  id: StatusSolicitacao | undefined,
  name: TranslateResult
}

interface IFormularioLiderados {
  categoria: string
  loaded: object[]
  selected: object[]
  dataInicial: Date | undefined
  dataFinal: Date | undefined
}

export interface IAbaDeNavegacao {
  id: number
  name: TranslateResult
  tipo: TipoRequisicao
}

/**
 * Interface do estado global deste módulo Vuex
 * @property dataInicial - Data inicial do período selecionado
 * @property dataFinal - Data final do período selecionado
 * @property categorias - Objeto contendo categorias disponíveis e selecionada
 */
export interface IStateGestaoDeFerias {
  dataInicial: Date
  dataFinal: Date
  solicitadasMeusLideradosSelecionadas: IFormularioLiderados
  agendadasMeusLideradosSelecionadas: IFormularioLiderados
  categorias: {
    loaded: Categoria[]
    selected: Categoria
  }
  statusSolicitacao: {
    loaded: IStatusSolicitacao[]
    selected: IStatusSolicitacao
  }
  abasMinhasFerias: IAbaDeNavegacao[]
  abasMeusLiderados: IAbaDeNavegacao[]
  abaSelecionada: IAbaDeNavegacao
  contextoDeEntrada: IContextoDeEntradaGestaoDeFerias | null
  calendarioDeFerias: IStateCalendarioDeFerias
}

export interface IStoreGestaoDeFerias {
  gestaoDeFerias: IStateGestaoDeFerias
}


export enum TipoRequisicao {
  agendadas = 'agendadas',
  solicitadas = 'solicitadas'
}

const categorias = [
  {
    name: i18n.t('comuns.genericos.plural.locaisDeTrabalho').toString(),
    id: 'companies',
    common: 'companies/onlyActive',
    state: 'gestaoDeFerias/companies',
    entity: 'local_de_trabalho'
  },
  {
    name: i18n.t('comuns.genericos.plural.empregados').toString(),
    id: 'employees',
    common: 'employees/onlyNotFired',
    state: 'gestaoDeFerias/employees',
    entity: 'empregado'
  },
  {
    name: i18n.t('comuns.genericos.plural.grupos').toString(),
    id: 'userGroups',
    common: 'userGroups',
    state: 'gestaoDeFerias/userGroups',
    entity: 'grupo'
  },
  {
    name: i18n.t('comuns.genericos.plural.perfis').toString(),
    id: 'roleGroups',
    common: 'roleGroups/onlyNotRh',
    state: 'gestaoDeFerias/roleGroups',
    entity: 'perfil'
  }
]

export const abasMeusLiderados: IAbaDeNavegacao[] = [
  {
    id: 0,
    name: i18n.t('gerenciamentoDeFerias.abas.solicitacoesDeFerias'),
    tipo: TipoRequisicao.solicitadas
  },
  {
    id: 1,
    name: i18n.t('gerenciamentoDeFerias.abas.feriasMeusLideradosJaProgramadas'),
    tipo: TipoRequisicao.agendadas
  },
]

export const abasMinhasFerias: IAbaDeNavegacao[] = [
  {
    id: 2,
    name: i18n.t('gerenciamentoDeFerias.abas.minhasSolicitacoesDeFerias'),
    tipo: TipoRequisicao.solicitadas
  },
  {
    id: 3,
    name: i18n.t('gerenciamentoDeFerias.abas.minhasFeriasJaProgramadas'),
    tipo: TipoRequisicao.agendadas
  }
]

const statusSolicitacao: IStatusSolicitacao[] = [
  { chave: 'todos', id: undefined, name: i18n.t('gerenciamentoDeFerias.mostrarStatus.todos') },
  { chave: String(StatusSolicitacao.Cancelado), id: StatusSolicitacao.Cancelado ,name: i18n.t('gerenciamentoDeFerias.mostrarStatus.cancelado') },
  { chave: String(StatusSolicitacao.Pendente), id: StatusSolicitacao.Pendente, name: i18n.t('gerenciamentoDeFerias.mostrarStatus.pendente') },
  { chave: String(StatusSolicitacao.Aprovado), id: StatusSolicitacao.Aprovado, name: i18n.t('gerenciamentoDeFerias.mostrarStatus.aprovado') },
  { chave: String(StatusSolicitacao.Recusado), id: StatusSolicitacao.Recusado, name: i18n.t('gerenciamentoDeFerias.mostrarStatus.reprovado') },
  { chave: String(StatusSolicitacao.ComObservacao), id: StatusSolicitacao.ComObservacao, name: i18n.t('gerenciamentoDeFerias.mostrarStatus.comObservacao') }
]

function estadoPadrao(): IStateGestaoDeFerias {
  return {
    ...multiSelect.mapStates(['companies', 'employees', 'userGroups', 'roleGroups']),
    dataInicial: moment().startOf('month').toDate(),
    dataFinal: moment().endOf('month').utc(true).toDate(),
    solicitadasMeusLideradosSelecionadas: {
      categoria: '',
      loaded: [],
      selected: []
    },
    agendadasMeusLideradosSelecionadas: {
      loaded: [],
      selected: []
    },
    categorias: {
      loaded: categorias,
      selected: categorias[0]
    },
    statusSolicitacao: {
      loaded: statusSolicitacao,
      selected: statusSolicitacao[0]
    },
    abasMinhasFerias,
    abasMeusLiderados,
    abaSelecionada: abasMeusLiderados[0],
    contextoDeEntrada: null
  }
}

const state = estadoPadrao()

const mutations = {
  ...multiSelect.mapMutations(['companies', 'employees', 'userGroups', 'roleGroups']),
  dataInicial: (estado: IStateGestaoDeFerias, payload: Moment) => estado.dataInicial = obterDataValida(payload),
  dataFinal: (estado: IStateGestaoDeFerias, payload: Moment) => estado.dataFinal = obterDataValida(payload),
  categorias: (estado: IStateGestaoDeFerias, payload: { loaded: Categoria[], selected: Categoria }) => estado.categorias = payload,
  solicitadasMeusLideradosSelecionadas: (estado: IStateGestaoDeFerias, payload: IFormularioLiderados) => estado.solicitadasMeusLideradosSelecionadas = payload,
  resetarEstado: (estado: IStateGestaoDeFerias) => Object.assign(estado, estadoPadrao()),
  agendadasMeusLideradosSelecionadas: (estado: IStateGestaoDeFerias, payload: IFormularioLiderados) => estado.agendadasMeusLideradosSelecionadas = payload,
  statusSolicitacao: (estado: IStateGestaoDeFerias, payload: { loaded: IStatusSolicitacao[], selected: IStatusSolicitacao}) => estado.statusSolicitacao = payload,
  abaSelecionada: (estado: IStateGestaoDeFerias, payload: IAbaDeNavegacao) => estado.abaSelecionada = payload,
  contextoDeEntrada: (estado: IStateGestaoDeFerias, payload: IContextoDeEntradaGestaoDeFerias | null) => estado.contextoDeEntrada = payload
}

export const actions = {
  aplicarContextoDeEntrada(
    { state: estado, commit }: ActionContext<IStateGestaoDeFerias, unknown>,
    aba: IAbaDeEntradaGestaoDeFerias
  ): boolean {
    const contexto = estado.contextoDeEntrada
    if (!contexto) return false

    const categoriaEmpregados = estado.categorias.loaded.find(categoria => categoria.id === 'employees')
    const datasValidas = [contexto.dataInicial, contexto.dataFinal]
      .every(data => data instanceof Date && !Number.isNaN(data.getTime()))
    const contextoValido = contexto.aba === aba &&
      Boolean(contexto.empregado?.id) &&
      Boolean(contexto.empregado?.name) &&
      datasValidas &&
      Boolean(categoriaEmpregados)

    // O handoff é de uso único, inclusive quando inválido ou incompatível com a aba.
    commit('contextoDeEntrada', null)
    if (!contextoValido || !categoriaEmpregados) return false

    const estadoDoEmpregado = {
      loaded: [contexto.empregado],
      selected: [contexto.empregado]
    }
    const filtrosSelecionados = {
      ...estadoDoEmpregado,
      categoria: categoriaEmpregados.id,
      dataInicial: contexto.dataInicial,
      dataFinal: contexto.dataFinal
    }
    const mutationDosFiltros = aba === 'liderados-solicitadas'
      ? 'solicitadasMeusLideradosSelecionadas'
      : 'agendadasMeusLideradosSelecionadas'

    commit('dataInicial', contexto.dataInicial)
    commit('dataFinal', contexto.dataFinal)
    commit('categorias', {
      loaded: estado.categorias.loaded,
      selected: categoriaEmpregados
    })
    commit('employees', estadoDoEmpregado)
    commit(mutationDosFiltros, filtrosSelecionados)

    return contexto.buscarAutomaticamente
  }
}

export default {
  namespaced: true,
  mutations,
  actions,
  state,
  modules: {
    calendarioDeFerias: CalendarioDeFeriasState
  }
}
